import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { PosTerminalService, ToastMessageService } from '@fitness-force/api';
import type { PosTerminal } from '@fitness-force/models';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SkeletonModule } from 'primeng/skeleton';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface PosDeviceListCloseEvent {
  hidePopup: boolean;
  selectedDevice: PosTerminal | null;
  emptyList?: boolean;
}

@Component({
  selector: 'pos-device-list',
  templateUrl: './pos-device-list.component.html',
  styleUrls: ['./pos-device-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    RadioButtonModule,
    ButtonModule,
    SkeletonModule,
    TranslateModule,
  ],
})
export class PosDeviceListComponent {
  private readonly fb = inject(FormBuilder);
  private readonly posTerminalService = inject(PosTerminalService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = input<boolean>(false, { alias: 'visible' });
  readonly tenantId = input.required<number>();
  readonly activeOnly = input<boolean | undefined>(undefined);
  readonly pgwConfMstId = input<number | undefined>(undefined);
  readonly autoSelectSingle = input<boolean>(false);
  readonly loadReady = input(true);

  readonly closeDevicesPopup = output<PosDeviceListCloseEvent>();

  deviceForm = this.fb.group({
    selectedDevice: [null as PosTerminal | null, Validators.required],
  });

  terminals: PosTerminal[] = [];
  loading = false;
  readonly showDialog = signal(false);
  private terminalsLoadRequested = false;

  constructor() {
    effect(() => {
      const isVisible = this.visible();
      const tenantId = this.tenantId();
      const ready = this.loadReady();

      if (!isVisible) {
        this.showDialog.set(false);
        this.terminalsLoadRequested = false;
        return;
      }

      this.showDialog.set(true);

      if (!tenantId) {
        return;
      }

      if (!ready) {
        this.loading = true;
        this.terminals = [];
        this.deviceForm.reset();
        this.cdr.markForCheck();
        return;
      }

      if (!this.terminalsLoadRequested) {
        this.terminalsLoadRequested = true;
        this.loadTerminals();
      }
    });
  }

  private loadTerminals(): void {
    this.loading = true;
    this.terminals = [];
    this.deviceForm.reset();
    this.cdr.markForCheck();

    const params: { active_only?: boolean; pgwconfmst_id?: number } = {};
    const activeOnly = this.activeOnly();
    const pgwConfMstId = this.pgwConfMstId();

    if (activeOnly !== undefined) {
      params.active_only = activeOnly;
    }
    if (pgwConfMstId !== undefined) {
      params.pgwconfmst_id = pgwConfMstId;
    }

    this.posTerminalService
      .getPosTerminals(this.tenantId(), params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (terminals) => {
          this.terminals = terminals ?? [];
          this.loading = false;

          if (this.terminals.length === 0) {
            this.toastMessageService.showErrorMessage(
              'No active POS terminal configured'
            );
            this.closeDevicesPopup.emit({
              hidePopup: true,
              selectedDevice: null,
              emptyList: true,
            });
            this.cdr.markForCheck();
            return;
          }

          if (this.autoSelectSingle() && this.terminals.length === 1) {
            this.closeDevicesPopup.emit({
              hidePopup: true,
              selectedDevice: this.terminals[0],
            });
            this.cdr.markForCheck();
            return;
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          this.loading = false;
          this.toastMessageService.showError(error);
          this.closeDevicesPopup.emit({
            hidePopup: true,
            selectedDevice: null,
            emptyList: true,
          });
          this.cdr.markForCheck();
        },
      });
  }

  selectDevice(device: PosTerminal): void {
    this.deviceForm.get('selectedDevice')?.setValue(device);
    this.cdr.markForCheck();
  }

  isDeviceSelected(device: PosTerminal): boolean {
    return this.deviceForm.get('selectedDevice')?.value?.id === device.id;
  }

  selectPosDevice(): void {
    if (!this.deviceForm.valid) {
      this.deviceForm.markAllAsTouched();
      return;
    }

    const selectedDevice = this.deviceForm.get('selectedDevice')!.value;
    this.closeDevicesPopup.emit({
      hidePopup: true,
      selectedDevice,
    });
  }

  closePosDevice(): void {
    this.closeDevicesPopup.emit({
      hidePopup: true,
      selectedDevice: null,
    });
  }

  closeChargeOnTerminalModal(): void {
    this.closeDevicesPopup.emit({
      hidePopup: true,
      selectedDevice: null,
    });
  }
}
